import { NavLink, useLocation } from 'react-router-dom';
import {
  BookOpen,
  Briefcase,
  Building2,
  CheckCircle2,
  CheckSquare,
  Cog,
  IdCard,
  KeyRound,
  LayoutDashboard,
  Lightbulb,
  Link2,
  Lock,
  Mail,
  Play,
  Server,
  Shield,
  SlidersHorizontal,
  Sparkles,
  Star,
  Table,
  Tags,
  Users,
  Wallet,
  Waypoints,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useApp } from '../../contexts/AppContext';
import { Logo } from './Logo';

interface NavItem {
  label: string;
  path: string;
  page: string;
  icon: LucideIcon;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

function item(label: string, icon: LucideIcon, path: string, page: string): NavItem {
  return { label, icon, path, page };
}

export function navSections(can: (page: string, action: string) => boolean): NavSection[] {
  const keep = (items: NavItem[]) => items.filter((i) => can(i.page, 'read'));
  const sections: NavSection[] = [
    {
      title: 'Overview',
      items: keep([
        item('Dashboard', LayoutDashboard, '/dashboard', 'dashboard'),
        item('Tenants', Building2, '/tenants', 'tenants'),
        item('Users', IdCard, '/users', 'users'),
        item('Groups', Lock, '/groups', 'groups'),
        item('Workspaces', Briefcase, '/workspaces', 'workspaces'),
      ]),
    },
    {
      title: 'Design',
      items: keep([
        item('AI Trainer', Sparkles, '/builder', 'builder'),
        item('Agents', Users, '/agents', 'agents'),
        item('Org & Hierarchy', Waypoints, '/org', 'org'),
        item('Tools', Wrench, '/tools', 'tools'),
        item('Skills', Star, '/skills', 'skills'),
        item('Workflow Types', SlidersHorizontal, '/workflow-types', 'workflow_types'),
        item('Taxonomy', Tags, '/taxonomy', 'taxonomy'),
      ]),
    },
    {
      title: 'Connect',
      items: keep([
        item('Triggers', Zap, '/triggers', 'triggers'),
        item('Integrations', Link2, '/integrations', 'integrations'),
        item('MCP Servers', Server, '/mcp-servers', 'mcp'),
        item('Knowledge', BookOpen, '/knowledge', 'knowledge'),
      ]),
    },
    {
      title: 'Operate',
      items: keep([
        item('Collaborate', Users, '/collaborate', 'collaborate'),
        item('Mailbox', Mail, '/mailbox', 'mailbox'),
        item('Payment orders', Wallet, '/payments', 'payments'),
        item('Runs', Play, '/runs', 'runs'),
        item('Reports', Table, '/reports', 'runs'),
      ]),
    },
    {
      title: 'Govern',
      items: keep([
        item('Review', Shield, '/review', 'review'),
        item('Approvals', CheckSquare, '/approvals', 'approvals'),
        item('Evaluations', CheckCircle2, '/evals', 'evals'),
        item('Evolution', Lightbulb, '/learning', 'learning'),
      ]),
    },
  ];
  const platform = keep([
    item('Cost Analytics', Table, '/costs', 'costs'),
    item('Identity Providers', KeyRound, '/admin/identity-providers', 'identity_providers'),
    item('Platform Settings', Cog, '/admin/settings', 'platform_settings'),
  ]);
  if (platform.length) sections.push({ title: 'Platform', items: platform });
  return sections.filter((s) => s.items.length);
}

export function activeNav(pathname: string, sections: NavSection[]): { section: string; label: string } {
  for (const section of sections) {
    const hit = section.items.find((i) => pathname === i.path || pathname.startsWith(`${i.path}/`));
    if (hit) return { section: section.title, label: hit.label };
  }
  return { section: 'Workspace', label: 'OrqForge' };
}

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { can, tenants, tenantId, workspaces, workspaceId } = useApp();
  const sections = navSections(can);
  const tenant = tenants.find((t) => t.id === tenantId);
  const workspace = workspaces.find((w) => w.id === workspaceId);
  return (
    <aside className="flex h-full w-72 flex-col border-r border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-950">
      <div className="flex h-16 items-center gap-2 border-b border-gray-200 px-5 dark:border-gray-800">
        <Logo />
        <span className="ml-auto rounded-full border border-gray-200 px-2 py-0.5 text-[0.6rem] uppercase tracking-wider text-gray-400 dark:border-gray-700">
          beta
        </span>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {sections.map((section) => (
          <div key={section.title}>
            <div className="mb-1.5 mt-3 px-3 text-[0.66rem] font-semibold uppercase tracking-[0.14em] text-gray-400 first:mt-0">
              {section.title}
            </div>
            <ul className="mb-2 flex flex-col gap-0.5">
              {section.items.map((nav) => (
                <li key={nav.path}>
                  <NavLink
                    to={nav.path}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100',
                        isActive && 'bg-blue-600 font-semibold text-white hover:bg-blue-600 hover:text-white dark:bg-blue-500',
                      )
                    }
                  >
                    <nav.icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{nav.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="border-t border-gray-200 p-3 dark:border-gray-800">
        <div className="rounded-lg bg-white px-3 py-2 text-xs text-gray-500 dark:bg-gray-900 dark:text-gray-400">
          <div className="truncate font-medium text-gray-800 dark:text-gray-200">{tenant?.name || 'OrqForge'}</div>
          <div className="truncate text-[0.68rem]">{workspace?.name || 'No workspace'}</div>
        </div>
      </div>
    </aside>
  );
}

export function usePageTitle(): { section: string; label: string } {
  const { pathname } = useLocation();
  const { can } = useApp();
  return activeNav(pathname, navSections(can));
}
